import { useState, useEffect, useCallback } from "react";

/**
 * Custom Hook for responsive font size with smooth transitions and error handling.
 * @param {Object} config - Configuration for responsiveness.
 * @param {number} config.baseWidth - Base screen width for scaling.
 * @param {number} config.baseHeight - Base screen height for scaling.
 * @param {number} config.baseFontSize - Default font size.
 * @param {number} config.minFontSize - Minimum font size.
 * @param {number} config.maxFontSize - Maximum font size.
 */
const useSmoothResponsiveFontSize = ({
  baseWidth = 1920,
  baseHeight = 1080,
  baseFontSize = 24,
  minFontSize = 12,
  maxFontSize = 36,
} = {}) => {
  const [fontSize, setFontSize] = useState(baseFontSize);

  const calculateFontSize = useCallback(() => {
    try {
      // Ensure browser environment
      if (typeof window === "undefined") {
        console.error("Window object is not available.");
        return;
      }

      const widthRatio = window.innerWidth / baseWidth;
      const heightRatio = window.innerHeight / baseHeight;

      // Weighted ratio: prioritizing width
      const weightedRatio = widthRatio * 0.75 + heightRatio * 0.25;

      const newFontSize = Math.min(
        Math.max(baseFontSize * weightedRatio, minFontSize),
        maxFontSize
      );
      // Update font size only if significantly different
      if (Math.abs(newFontSize-fontSize ) > 0.05) {
        setFontSize(newFontSize);
      }
    } catch (error) {
      console.error("Error calculating font size:", error);
    }
  }, [fontSize, baseWidth, baseHeight, baseFontSize, minFontSize, maxFontSize]);

  useEffect(() => {
    let animationFrame;

    const resizeHandler = () => {
      try {
        if (animationFrame) cancelAnimationFrame(animationFrame);
        animationFrame = requestAnimationFrame(calculateFontSize);
      } catch (error) {
        console.error("Error in resize handler:", error);
      }
    };

    try {
      window.addEventListener("resize", resizeHandler);
      calculateFontSize(); // Initial calculation
    } catch (error) {
      console.error("Error setting up resize listener:", error);
    }

    return () => {
      try {
        window.removeEventListener("resize", resizeHandler);
        if (animationFrame) cancelAnimationFrame(animationFrame);
      } catch (error) {
        console.error("Error cleaning up resize listener:", error);
      }
    };
  }, [calculateFontSize]);

  return fontSize;
};
export default useSmoothResponsiveFontSize;
